import { ForbiddenCodeEnum } from '@/context/services/type';
import { useCheckInvoiceReportPermission } from '@/features/invoice_reports/components/hooks/useCheckInvoiceReportPermission';
import { useCheckInvoiceReportsProtectedResources } from '@/features/invoice_reports/components/hooks/useCheckInvoiceReportsProtectedResources';
import { useTranslation } from '@/i18n';
import { toHtmlNode } from '@/utils/toHtmlNode';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback, useEffect, useState } from 'react';

const TOAST_DURATION = 5;

export enum AgentEntryOpenDetail {
  InvoiceReport = 'invoice_report',
  InvoiceReportBulkUpload = 'invoice_report_bulk_upload',
}

type AgentEntryOpenMessage = {
  type: 'ap:agent-entry:open';
  requestId: string;
  reportTypeKey: AgentEntryOpenDetail;
};

export enum AgentEntryResultStatus {
  OK = 'ok',
  FORBIDDEN = 'forbidden',
  ERROR = 'error',
}

type AgentEntryResultMessage = {
  type: 'ap:agent-entry:result';
  requestId: string;
  status: AgentEntryResultStatus;
  code?: string;
  messages?: string[];
};

type PendingRequest = {
  requestId: string;
  origin: string;
};

function isAgentEntryResultMessage(
  data: unknown
): data is AgentEntryResultMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    'requestId' in data
  );
}

export function useOpenAgentEntryModal(): (
  reportTypeKey: AgentEntryOpenDetail
) => void {
  const notify = useToastAPI();
  const { t } = useTranslation();
  const checkPermission = useCheckInvoiceReportPermission();
  const { handleCheckPermission } = useCheckInvoiceReportsProtectedResources();
  const [pending, setPending] = useState<PendingRequest | null>(null);

  useEffect(() => {
    if (!pending) return;
    const { requestId, origin } = pending;

    async function handleMessage(event: MessageEvent) {
      if (event.origin !== origin) return;
      if (!isAgentEntryResultMessage(event.data)) return;
      const data = event.data;
      if (data.type !== 'ap:agent-entry:result' || data.requestId !== requestId)
        return;

      setPending(null);
      const { status, code, messages } = data;

      if (status === AgentEntryResultStatus.FORBIDDEN) {
        if (code === ForbiddenCodeEnum.FORBIDDEN_MIMIC_MODE) {
          notify.error({
            title: toHtmlNode(t('invoice_reports.error.forbidden_mimic_mode')),
            duration: TOAST_DURATION,
          });
          return;
        }
        const errorTitle = messages?.join('\n');
        await handleCheckPermission({
          fnCheckPermission: checkPermission,
          onCheckSuccess: () => {
            if (errorTitle) {
              notify.error({
                title: toHtmlNode(errorTitle),
                duration: TOAST_DURATION,
              });
            }
          },
          onCheckFailure: () => {
            location.reload();
          },
        });
        return;
      }

      if (status !== AgentEntryResultStatus.OK) location.reload();
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [pending, notify, t, checkPermission, handleCheckPermission]);

  return useCallback((reportTypeKey: AgentEntryOpenDetail) => {
    const origin = window.location.origin;
    const target = window.parent !== window ? window.parent : window;
    const requestId = `${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2)}`;

    const msg: AgentEntryOpenMessage = {
      type: 'ap:agent-entry:open',
      requestId,
      reportTypeKey,
    };
    target.postMessage(msg, origin);
    setPending({ requestId, origin });
  }, []);
}
